import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Globe, ChevronDown, X } from 'lucide-react';
import { setLanguage } from '../../actions';
import './GoogleTranslation.css';

const languages = { en: 'English', af: 'Afrikaans', fr: 'Français', ha: 'Hausa', ig: 'Igbo', yo: 'Yorùbá', zu: 'isiZulu' };
let translatorScript;
let widgetCount = 0;

function loadTranslator() {
  if (window.google?.translate?.TranslateElement) return Promise.resolve();
  if (!translatorScript) {
    translatorScript = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      const fail = () => {
        clearTimeout(timeout);
        script.remove();
        translatorScript = null;
        reject(new Error('Translation could not load. Check your connection or content blocker.'));
      };
      const timeout = setTimeout(fail, 15000);
      window.googleTranslateElementInit = () => { clearTimeout(timeout); resolve(); };
      script.onerror = fail;
      document.head.appendChild(script);
    });
  }
  return translatorScript;
}

function savedLanguage() {
  try {
    const cookie = document.cookie.match(/(?:^|;\s*)googtrans=([^;]*)/);
    const language = cookie && decodeURIComponent(cookie[1]).split('/').pop();
    return languages[language] ? language : null;
  } catch (_) { return null; }
}

export default function GoogleTranslation() {
  const dispatch = useDispatch();
  const currentLanguage = useSelector(state => state.currentLanguage);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('idle');
  const [attempt, setAttempt] = useState(0);
  const container = useRef(null);
  const widget = useRef(null);
  const trigger = useRef(null);
  const initialized = useRef(false);
  const id = useRef(null);
  if (!id.current) id.current = `phis-translation-${++widgetCount}`;

  useEffect(() => {
    const language = savedLanguage();
    if (language && language !== currentLanguage) dispatch(setLanguage(language));
    // Google persists the user's choice in its own googtrans cookie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const element = widget.current;
    function change(event) {
      if (!event.target.matches('.goog-te-combo')) return;
      dispatch(setLanguage(languages[event.target.value] ? event.target.value : 'en'));
      setOpen(false);
      trigger.current?.focus();
    }
    element.addEventListener('change', change);
    return () => element.removeEventListener('change', change);
  }, [dispatch]);

  useEffect(() => {
    if (!open || initialized.current) return;
    let cancelled = false;
    setStatus('loading');
    loadTranslator().then(() => {
      if (cancelled || !widget.current || initialized.current) return;
      new window.google.translate.TranslateElement({
        pageLanguage: 'en', includedLanguages: Object.keys(languages).join(','), autoDisplay: false,
      }, id.current);
      initialized.current = true;
      setStatus('ready');
    }).catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [open, attempt]);

  useEffect(() => {
    if (!open) return;
    function outside(event) { if (!container.current?.contains(event.target)) setOpen(false); }
    function escape(event) { if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); } }
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);

  return <div className="phis-language notranslate" ref={container} translate="no">
    <button className="phis-language-trigger" type="button" ref={trigger} aria-label={`Language: ${languages[currentLanguage] || 'English'}`} aria-expanded={open} aria-controls={`${id.current}-panel`} onClick={() => setOpen(value => !value)}>
      <Globe size={17} aria-hidden="true" /><span>{languages[currentLanguage] || 'English'}</span><ChevronDown size={14} aria-hidden="true" />
    </button>
    <section className="phis-language-panel" id={`${id.current}-panel`} hidden={!open} aria-label="Translate this page">
      <div className="phis-language-heading"><div><h2>Page language</h2><p>Choose your reading language</p></div><button type="button" aria-label="Close language selector" onClick={() => { setOpen(false); trigger.current?.focus(); }}><X size={16} /></button></div>
      {status === 'loading' && <p className="phis-language-status" role="status">Loading languages…</p>}
      {status === 'error' && <div className="phis-language-error"><p role="alert">Translation could not load. Check your connection or content blocker.</p><button type="button" onClick={() => setAttempt(value => value + 1)}>Try again</button></div>}
      <div ref={widget} id={id.current} className="phis-language-widget" />
      {status === 'ready' && <p className="phis-language-note">Automatic translation. The original page is in English.</p>}
    </section>
  </div>;
}
